import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';

type AuthContextType = {
  token: string | null;
  login: (accessToken: string, refreshToken: string) => void;
  logout: (reason?: string) => void;
  showExpirationModal: boolean;
  closeExpirationModal: () => void;
};

type JwtPayload = {
  exp: number;
};

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [showExpirationModal, setShowExpirationModal] = useState(false);
  const navigate = useNavigate();

  const login = useCallback((accessToken: string, refreshToken: string) => {
    setToken(accessToken);
    localStorage.setItem('token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
  }, []);

  const logout = useCallback((reason?: string) => {
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    if (reason === 'expired') {
      setShowExpirationModal(true);
    } else {
      navigate('/login');
    }
  }, []);

  const closeExpirationModal = useCallback(() => {
    setShowExpirationModal(false);
    navigate('/login');
  }, [navigate]);


  return (
    <AuthContext.Provider value={{
      token,
      login,
      logout,
      showExpirationModal,
      closeExpirationModal
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);